import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardMedia,
} from '@mui/material';
import { Upload, Save, Image as ImageIcon } from 'lucide-react';
import { getSettings, updateSettings, SiteSettings } from '../../firebase/settings';
import { uploadSiteImage } from '../../firebase/storage';
import toast from 'react-hot-toast';

const SettingsManager = () => {
  const [settings, setSettings] = useState<SiteSettings>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newImage, setNewImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await getSettings();
      setSettings(data);
    } catch (error) {
      console.error('Error loading settings:', error);
      setError('Error al cargar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Validation
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('La imagen no debe superar los 5MB');
        return;
      }

      if (!file.type.startsWith('image/')) {
        toast.error('El archivo debe ser una imagen');
        return;
      }

      setNewImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      let imageUrl = settings.heroImage;

      if (newImage) {
        imageUrl = await uploadSiteImage(newImage);
      }

      await updateSettings({
        heroImage: imageUrl,
      });

      setSettings(prev => ({ ...prev, heroImage: imageUrl }));
      setNewImage(null);
      setPreviewUrl(null);
      toast.success('Configuración guardada correctamente');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom className="font-orbitron text-white mb-6">
        Configuración de la Web
      </Typography>

      <Paper sx={{ p: 4, bgcolor: '#1a1a1a', color: 'white', borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ color: '#00ffff', mb: 3 }}>
          Imagen Principal (Hero)
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ color: '#aaa', mb: 2 }}>
                Sube una imagen .png transparente para el encabezado principal.
                Se recomienda un tamaño máximo de 5MB.
              </Typography>
              
              <input
                accept="image/png, image/jpeg, image/webp"
                style={{ display: 'none' }}
                id="hero-image-upload"
                type="file"
                onChange={handleImageChange}
              />
              <label htmlFor="hero-image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<Upload />}
                  sx={{
                    color: '#00ffff',
                    borderColor: '#00ffff',
                    '&:hover': {
                      borderColor: '#ffffff',
                      bgcolor: 'rgba(0, 255, 255, 0.1)',
                    },
                  }}
                >
                  Seleccionar Imagen
                </Button>
              </label>
            </Box>

            {newImage && (
              <Typography variant="body2" sx={{ color: '#00ff00', mt: 1 }}>
                Archivo seleccionado: {newImage.name}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>
              Vista Previa:
            </Typography>
            <Card sx={{ bgcolor: 'black', border: '1px solid #333' }}>
              {(previewUrl || settings.heroImage) ? (
                <CardMedia
                  component="img"
                  image={previewUrl || settings.heroImage}
                  alt="Hero Preview"
                  sx={{
                    height: 200,
                    objectFit: 'contain',
                    p: 2,
                    bgcolor: 'black'
                  }}
                />
              ) : (
                <Box sx={{ 
                  height: 200, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  bgcolor: 'black',
                  color: '#444'
                }}>
                  <ImageIcon size={48} />
                </Box>
              )}
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSave}
                disabled={saving}
                sx={{
                  bgcolor: '#00ffff',
                  color: 'black',
                  '&:hover': {
                    bgcolor: '#ffffff',
                  },
                }}
              >
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default SettingsManager;
